import Modal from 'antd/es/modal/Modal';

import { hasValue } from 'utils/hasValue';

import type { WebhookCreateResponseConflictsItem } from 'api';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';
import { SecondaryButton } from '@atoms/buttons/SecondaryButton';

import {
  REACTIVATE_WEBHOOK_CONFLICT_BODY_INTRO,
  REACTIVATE_WEBHOOK_CONFLICT_BODY_OUTRO,
  REACTIVATE_WEBHOOK_CONFLICT_CONFIRM_TEST_ID,
  REACTIVATE_WEBHOOK_CONFLICT_DISMISS_TEST_ID,
  REACTIVATE_WEBHOOK_CONFLICT_MODAL_TEST_ID
} from './constants';
import { useReactivateWebhooks } from './useReactivateWebhooks';
import { reactivateWebhookConflictTitle } from './utils';

type ReactivateWebhookConflictModalProps = {
  conflicts: WebhookCreateResponseConflictsItem[];
  open: boolean;
  onClose: () => void;
};

const MODAL_WIDTH = 480;

export const ReactivateWebhookConflictModal = ({
  conflicts,
  open,
  onClose
}: ReactivateWebhookConflictModalProps) => {
  const { reactivate, isPending } = useReactivateWebhooks();
  const count = conflicts.length;
  const uuids = conflicts.map((conflict) => conflict.uuid).filter(hasValue);

  const handleReactivate = () => reactivate(uuids, onClose);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={MODAL_WIDTH}
      destroyOnHidden
      title={
        <div className="text-lg font-medium text-content-primary">
          {reactivateWebhookConflictTitle(count)}
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <SecondaryButton
            data-testid={REACTIVATE_WEBHOOK_CONFLICT_DISMISS_TEST_ID}
            onClick={onClose}
          >
            Dismiss
          </SecondaryButton>
          <PrimaryButton
            data-testid={REACTIVATE_WEBHOOK_CONFLICT_CONFIRM_TEST_ID}
            disabled={isPending}
            onClick={handleReactivate}
          >
            Reactivate
          </PrimaryButton>
        </div>
      }
    >
      <div
        data-testid={REACTIVATE_WEBHOOK_CONFLICT_MODAL_TEST_ID}
        className="flex flex-col gap-2 text-base text-content-primary"
      >
        <p>{REACTIVATE_WEBHOOK_CONFLICT_BODY_INTRO}</p>
        <ul className="flex flex-col gap-1">
          {conflicts.map((conflict) => (
            <li
              key={conflict.uuid ?? conflict.event}
              className="font-mono text-content-primary"
            >
              {conflict.event}
            </li>
          ))}
        </ul>
        <p>{REACTIVATE_WEBHOOK_CONFLICT_BODY_OUTRO}</p>
      </div>
    </Modal>
  );
};
